import { WorkExperienceContractType } from '@flex-apis/recruiting';
import {
  FieldBox,
  FormField,
  PrimitiveButton,
  Select,
  Typography,
} from '@flex-design-system/fx';
import {
  ChevronDownMiniIcon,
  SignatureIcon,
} from '@flex-design-system/legacy-flex-icons';
import { Translation, useTranslation } from '@flex-packages/i18n';
import { trans } from '@flex-packages/i18n-base';
import { WORK_EXPERIENCE_CONTRACT_TYPE_CONTENT } from '@flex-private-packages/recruiting-common/base';
import { Controller } from 'react-hook-form';

import type { JobDescriptionFormState } from '../../../../models';

interface Props {
  currentIndex: number;
  required: boolean;
}

const WORK_EXPERIENCE_STATUS_TYPES: Array<WorkExperienceContractType> = [
  WorkExperienceContractType.Regular,
  WorkExperienceContractType.Contract,
  WorkExperienceContractType.Freelancer,
  WorkExperienceContractType.ShortTerm,
  WorkExperienceContractType.Intern,
  WorkExperienceContractType.Dispatch,
];

export function WorkExperienceContractTypeSelectFormItem({
  currentIndex,
  required,
}: Props) {
  const { t } = useTranslation();

  return (
    <Controller<
      JobDescriptionFormState,
      `workExperienceBackgrounds.${number}.contractType`
    >
      name={`workExperienceBackgrounds.${currentIndex}.contractType`}
      rules={{
        required: required
          ? t(
              trans(
                'recruiting.work_experience.contract_status.edit.placeholder'
              )
            )
          : false,
      }}
      render={({
        field: { value, onChange, ...field },
        fieldState: { error },
      }) => (
        <FormField required={required} state={error ? 'error' : 'default'}>
          <FormField.Label leftSlot={<SignatureIcon />}>
            <Translation
              tKey={trans('recruiting.work_experience.contract_status')}
            />
          </FormField.Label>
          <Select value={value} onValueChange={onChange} {...field}>
            <Select.Trigger asChild>
              <FormField.FieldBox asChild>
                <PrimitiveButton css={{ width: '100%' }}>
                  <FieldBox.Content>
                    {value ? (
                      <Translation
                        tKey={WORK_EXPERIENCE_CONTRACT_TYPE_CONTENT[value].i18n}
                      />
                    ) : (
                      <Typography color="gray" asChild>
                        <Translation
                          tKey={trans(
                            'recruiting.work_experience.contract_status.edit.placeholder'
                          )}
                        />
                      </Typography>
                    )}
                  </FieldBox.Content>
                  <FieldBox.RightSlotRoot>
                    <ChevronDownMiniIcon size={22} color="grayLight" />
                  </FieldBox.RightSlotRoot>
                </PrimitiveButton>
              </FormField.FieldBox>
            </Select.Trigger>
            <Select.Content fitToTrigger>
              {WORK_EXPERIENCE_STATUS_TYPES.map(statusType => (
                <Select.Option key={statusType} value={statusType}>
                  <Select.OptionTitle>
                    <Translation
                      tKey={
                        WORK_EXPERIENCE_CONTRACT_TYPE_CONTENT[statusType].i18n
                      }
                    />
                  </Select.OptionTitle>
                </Select.Option>
              ))}
            </Select.Content>
          </Select>
          {error?.message && (
            <FormField.HelperText>{error.message}</FormField.HelperText>
          )}
        </FormField>
      )}
    />
  );
}
